import { createReducer } from '@reduxjs/toolkit';
import {
  ErrorsInterface,
  IContractData,
  IContractToEdit,
  IContractsCountPerWeek,
  ICostEstimation,
} from 'modules/contracts/types';
import {
  contractCountAction,
  deleteContractsSuccessAction,
  getContractsInListViewPayloadAction,
  getCostEstimationPayloadAction,
  getDimonaNumberErrorAction,
  getDimonaNumberSuccessAction,
  setContractCardToCopyAction,
  setContractsCountFromPreviousWeeksAction,
  setContractsMultipleConfimrationPayload,
  setContractsMultipleConfirmationSucces,
  setContractsRequestsLoadingAction,
  setDirectlyCardToCopy,
  setErrors,
  setShouldCopyContracts,
  toggleCopyModeAction,
  tooManyContractsAction,
  validateContractErrorMessageAction,
  validateContractErrorMessageRowAction,
  validateContractSuccessAction,
} from 'store/actions/contract-actions';
import { DimonaContractKeyPair } from 'types/contract/DimonaDetails';
import { IContractCard } from 'types/contract/IContractCard';

export interface ContractPageState {
  pageSize: number;
  pageIndex: number;
  numberOfResults: number;
}

export interface IContractsState {
  contracts: IContractToEdit[];
  listViewContracts: IContractData[];
  mappedContracts: IContractData[];
  validateEditContractSuccess: boolean;
  validateContractErrorMessage: string;
  validateContractErrorMessageRow: number;
  errors: ErrorsInterface[];
  shouldCopy: boolean;
  directlyCardToCopy: number | null;
  isCopyModeEnabled: boolean;
  contractCardToCopy: IContractCard;
  deleteContractsSuccess: boolean;
  loading: boolean;
  costEstimation: ICostEstimation[];
  contractsFromPreviousWeeks: IContractsCountPerWeek[];
  multipleConfirmationResponse: any;
  multipleConfirmationSuccess: boolean;
  getDimonaSuccess?: DimonaContractKeyPair;
  getDimonaError?: DimonaContractKeyPair;
  tooManyContracts?: boolean;
  contractPageDetails: ContractPageState;
}

const initialState: IContractsState = {
  contracts: [],
  mappedContracts: [],
  listViewContracts: [],
  validateEditContractSuccess: false,
  validateContractErrorMessage: '',
  validateContractErrorMessageRow: null,
  errors: [],
  shouldCopy: false,
  directlyCardToCopy: null,
  isCopyModeEnabled: false,
  contractCardToCopy: null,
  deleteContractsSuccess: false,
  loading: false,
  costEstimation: null,
  contractsFromPreviousWeeks: null,
  multipleConfirmationResponse: [],
  multipleConfirmationSuccess: false,
  getDimonaSuccess: null,
  getDimonaError: null,
  tooManyContracts: false,
  contractPageDetails: null,
};

const generateErrorArray = (errors: ErrorsInterface[]) => {
  const auxErrors = errors.map((err: ErrorsInterface, i: number) => {
    if (err.type === 'deletePastContract' || err.type === 'signedPastContract')
      return {
        id: i,
        type: err.type,
        contractId: err.contractId,
      };
    return {
      id: i,
      type: err.type,
    };
  });
  return auxErrors;
};

const contractsReducer = createReducer(initialState, builder => {
  return builder
    .addCase(setContractsRequestsLoadingAction, (state, action) => {
      return {
        ...state,
        loading: action.payload,
      };
    })
    .addCase(getContractsInListViewPayloadAction, (state, action) => {
      return {
        ...state,
        listViewContracts: action.payload,
      };
    })
    .addCase(tooManyContractsAction, (state, action) => {
      return {
        ...state,
        tooManyContracts: action.payload,
      };
    })
    .addCase(contractCountAction, (state, action) => {
      return {
        ...state,
        contractPageDetails: action.payload,
      };
    })
    .addCase(getCostEstimationPayloadAction, (state, action) => {
      return {
        ...state,
        costEstimation: action.payload,
      };
    })
    .addCase(validateContractSuccessAction, (state, action) => {
      return {
        ...state,
        validateEditContractSuccess: action.payload,
      };
    })
    .addCase(validateContractErrorMessageAction, (state, action) => ({
      ...state,
      validateContractErrorMessage: action.payload,
    }))
    .addCase(validateContractErrorMessageRowAction, (state, action) => {
      return {
        ...state,
        validateContractErrorMessageRow: action.payload,
      };
    })
    .addCase(setErrors, (state, action) => {
      return {
        ...state,
        errors: generateErrorArray(action.payload),
      };
    })
    .addCase(setShouldCopyContracts, (state, action) => {
      return {
        ...state,
        shouldCopy: action.payload,
      };
    })
    .addCase(setDirectlyCardToCopy, (state, action) => {
      return {
        ...state,
        directlyCardToCopy: action.payload,
      };
    })
    .addCase(setContractCardToCopyAction, (state, action) => {
      return {
        ...state,
        contractCardToCopy: action.payload,
      };
    })
    .addCase(toggleCopyModeAction, state => {
      return {
        ...state,
        isCopyModeEnabled: !state.isCopyModeEnabled,
      };
    })
    .addCase(deleteContractsSuccessAction, (state, action) => {
      return {
        ...state,
        deleteContractsSuccess: action.payload,
      };
    })
    .addCase(setContractsCountFromPreviousWeeksAction, (state, action) => {
      return {
        ...state,
        contractsFromPreviousWeeks: action.payload,
      };
    })
    .addCase(setContractsMultipleConfimrationPayload, (state, action) => {
      return {
        ...state,
        multipleConfirmationResponse: action.payload,
      };
    })
    .addCase(setContractsMultipleConfirmationSucces, (state, action) => {
      return {
        ...state,
        multipleConfirmationSuccess: action.payload,
      };
    })
    .addCase(getDimonaNumberSuccessAction, (state, action) => {
      return {
        ...state,
        getDimonaSuccess: action.payload,
      };
    })
    .addCase(getDimonaNumberErrorAction, (state, action) => {
      return {
        ...state,
        getDimonaError: action.payload,
      };
    });
});

export default contractsReducer;
